import * as RC from '@radix-ui/react-checkbox'
import { Check, Minus } from 'lucide-react'
import { useId, type ComponentProps, type ReactNode } from 'react'
import { cn } from './cn'

export interface CheckboxProps extends ComponentProps<typeof RC.Root> {
  /** Optional label rendered to the right (clicking it toggles the box). */
  label?: ReactNode
  description?: ReactNode
}

/** Checkbox (Radix). Checked and indeterminate states use the primary fill. */
export function Checkbox({ label, description, className, id, ...rest }: CheckboxProps) {
  const autoId = useId()
  const boxId = id ?? `cb-${autoId}`
  const box = (
    <RC.Root
      id={boxId}
      className={cn(
        'peer inline-flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border border-line-strong bg-surface',
        'transition-colors duration-150 ease-out focus-ring hover:border-muted',
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-on-primary',
        'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-on-primary',
        'disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger',
        !label && className,
      )}
      {...rest}
    >
      <RC.Indicator className="flex items-center justify-center">
        {rest.checked === 'indeterminate' ? (
          <Minus className="size-3.5" strokeWidth={3} />
        ) : (
          <Check className="size-3.5" strokeWidth={3} />
        )}
      </RC.Indicator>
    </RC.Root>
  )
  if (!label) return box
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <div className="flex h-[21px] items-center">{box}</div>
      <label htmlFor={boxId} className="cursor-pointer text-body text-text select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
        {label}
        {description && <span className="mt-0.5 block text-meta text-muted">{description}</span>}
      </label>
    </div>
  )
}
